import globalIP from '@/GlobelIP';
import { handleDecrypt } from '@/functions';
import axios from 'axios';
import { create } from 'zustand';

interface SelectedProductsState {
  selectedItems: any[];
  handleCheckChange: (value: any, checked: boolean) => void;
  isSelectedLoading: boolean;
}
const loadFromLocalStorage = (): any[] => {
  const selectedItems = sessionStorage.getItem('selectedItems');
  return selectedItems ? JSON.parse(selectedItems) : [];
};
const saveToLocalStorage = (selectedItems: any[]): void => {
  sessionStorage.setItem('selectedItems', JSON.stringify(selectedItems));
};
const C_A_id = handleDecrypt(sessionStorage.getItem('E-C_A_id'));

const catalogueCode = handleDecrypt(sessionStorage.getItem('E-typecode'));
const CatalogLinkStatus = async status => {
  const data = await axios.put(
    `${globalIP}/api/catalogues/${handleDecrypt(sessionStorage.getItem('Cat_id'))}?populate=*`,
    {
      data: { LinkStatus: status }
    }
  );
};
const chunkArray = (array, size = 25) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const safePutWithRetry = async (
  url,
  payload,
  retries = 3,
  delayMs = 600
) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      await axios.put(url, payload);
      return true;
    } catch (err) {
      console.warn(`⚠️ Chunk failed (attempt ${attempt})`);

      if (attempt === retries) {
        console.error('❌ Chunk permanently failed:', err);
        return false;
      }

      await delay(delayMs);
    }
  }
};
const mergeShortlist = (existing, incoming) => {
  const map = new Map();

  [...existing, ...incoming].forEach(item => {
    map.set(item.productid, item);
  });

  return Array.from(map.values());
};
const removeFromShortlist = (existing, idsToRemove) => {
  const removeSet = new Set(idsToRemove.map(String));
  return existing.filter(item => !removeSet.has(item.productid));
};
const getSessionValues = () => ({
  C_A_id: handleDecrypt(sessionStorage.getItem('E-C_A_id')),
  catalogueCode: handleDecrypt(sessionStorage.getItem('E-typecode')),
});

export const useSelectedProducts = create<SelectedProductsState>(set => ({
  selectedItems: loadFromLocalStorage(),
  isSelectedLoading: false,
  handleCheckChange: async (value, checked, ProductCode) => {
    const { C_A_id, catalogueCode } = getSessionValues();

    if (!C_A_id) {
      console.warn('C_A_id not ready yet');
      return;
    }

    const encodedId = encodeURIComponent(C_A_id);

    try {
      set(state => ({ ...state, isSelectedLoading: true }));
      const response = await axios.get(`${globalIP}/api/clientactivities/${encodedId}?populate=*`);
      const shortlistproduct = response.data.data.attributes.shortlistproduct;

      let filterData;
      if (checked) {
        filterData = shortlistproduct.filter(item => item.productid != value);
        const updatedOpenedProductData = [
          ...filterData,
          {
            typecd: catalogueCode,
            productid: String(value),
            productcode: ProductCode
          }
        ];
        await axios.put(`${globalIP}/api/clientactivities/${encodedId}`, {
          data: {
            shortlistproduct: updatedOpenedProductData
          }
        });
      } else {
        filterData = shortlistproduct.filter(item => item.productid != value);
        if (filterData.length) {
          CatalogLinkStatus('Selected');
        } else {
          CatalogLinkStatus('Opened');
        }
        await axios.put(`${globalIP}/api/clientactivities/${encodedId}`, {
          data: {
            shortlistproduct: filterData
          }
        });
      }
    } catch (error) {
      console.error('Error:', error);
      set(state => ({ ...state, isSelectedLoading: false }));
    }

    set(state => {
      const updatedItems = checked
        ? [...state.selectedItems, value]
        : state.selectedItems.filter(item => item != value);
      saveToLocalStorage(updatedItems);
      return { selectedItems: updatedItems, isSelectedLoading: false };
    });
  },
  handleCleanSelectedItems: () => {
    saveToLocalStorage([]);
    set({ selectedItems: [] });
  },
  handleSelectAllIds: async (allProductData) => {
    const { C_A_id, catalogueCode } = getSessionValues();

    if (!C_A_id) {
      console.warn('C_A_id not ready yet');
      return;
    }

    const encodedId = encodeURIComponent(C_A_id);
    set({ isSelectedLoading: true });

    try {
      const allIds = allProductData.map(item => item.id);

      const isAllSelected = allIds.every(id =>
        loadFromLocalStorage().includes(id)
      );

      // 🔴 DESELECT ALL
      if (isAllSelected) {
        saveToLocalStorage([]);
        set({ selectedItems: [] });
        CatalogLinkStatus('Opened');

        const removeChunks = chunkArray(
          allIds.map(String),
          25
        );

        for (const chunk of removeChunks) {
          const res = await axios.get(
            `${globalIP}/api/clientactivities/${encodedId}?populate=*`
          );

          const existingShortlist =
            res.data?.data?.attributes?.shortlistproduct || [];

          const updatedShortlist = removeFromShortlist(
            existingShortlist,
            chunk
          );

          await axios.put(
            `${globalIP}/api/clientactivities/${encodedId}`,
            {
              data: {
                shortlistproduct: updatedShortlist,
              },
            }
          );

          await delay(400);
        }

        console.log('✅ Select All → Deselected slowly');
        return;
      }

      // 🟢 SELECT ALL
      saveToLocalStorage(allIds);
      set({ selectedItems: allIds });
      CatalogLinkStatus('Selected');

      const payload = allProductData.map(item => ({
        typecd: catalogueCode,
        productid: String(item.id),
        productcode: item.ProductCode || '',
      }));

      const addChunks = chunkArray(payload, 25);

      for (const chunk of addChunks) {
        const res = await axios.get(
          `${globalIP}/api/clientactivities/${encodedId}?populate=*`
        );

        const existingShortlist =
          res.data?.data?.attributes?.shortlistproduct || [];

        const merged = mergeShortlist(existingShortlist, chunk);

        await axios.put(
          `${globalIP}/api/clientactivities/${encodedId}`,
          {
            data: {
              shortlistproduct: merged,
            },
          }
        );

        await delay(400);
      }

      console.log('✅ Select All → Selected slowly');
    } catch (error) {
      console.error('❌ Select All toggle failed:', error);
    } finally {
      set({ isSelectedLoading: false });
    }
  }




}));
